import React, { type FC, type PropsWithChildren } from "react";

type HeadingLevels = 1 | 2 | 3 | 4 | 5 | 6;
type Headings = `h${HeadingLevels}`;

const extractTextContent = (children: React.ReactNode): string => {
	if (typeof children === "string") {
		return children;
	}
	if (typeof children === "number") {
		return children.toString();
	}
	if (Array.isArray(children)) {
		return children.map(extractTextContent).join("");
	}
	if (React.isValidElement<React.PropsWithChildren>(children)) {
		return extractTextContent(children.props.children);
	}
	return "";
};

const createHeading = (level: HeadingLevels) => {
	const Heading: FC<PropsWithChildren> = ({ children, ...props }) => {
		const text = extractTextContent(children);
		const id = text
			.toLowerCase()
			.trim()
			.replace(/\s+/g, "-")
			.replace(/[^\w-]+/g, "");

		const Tag: Headings = `h${level}`;
		return (
			<Tag id={id} {...props}>
				{children}
			</Tag>
		);
	};
	Heading.displayName = `Heading${level}`;

	return Heading;
};

export const components = {
	h1: createHeading(1),
	h2: createHeading(2),
	h3: createHeading(3),
	h4: createHeading(4),
	h5: createHeading(5),
	h6: createHeading(6),
} as const satisfies Record<Headings, FC>;
